import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { authAPI } from '../../api/authAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SEO from 'components/seo/SEO';
import styled from 'styled-components';
import {
  sanitizeUserText,
  MAX_INTEREST_ITEM_NAME,
  MAX_INTEREST_DESCRIPTION,
  MAX_PREFERENCE_TEXT,
} from '../../utils/sanitizeText';
import ContextualCoachBanner from '../../components/onboarding/ContextualCoachBanner';
import { PRE_OWNED_ITEM_CATEGORIES } from '../../constants/preOwnedItemCategories';

function getInterestErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data === 'object') {
    const parts = Object.entries(data).flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((entry) => (
          key === 'non_field_errors' ? String(entry) : `${key}: ${entry}`
        ));
      }
      if (typeof value === 'string') return [value];
      return [];
    });
    if (parts.length > 0) return parts.join(' ');
  }
  return fallback;
}

const InterestsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const SubtitleText = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0.5rem 0 0 0;
  font-weight: 400;
`;

const AddButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border-radius: 0.625rem;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(102, 126, 234, 0.4);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f0f0f0;
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.primary ? `
    background-color: var(--primary-color);
    color: white;
    &:hover {
      background-color: var(--primary-dark);
    }
  ` : `
    background-color: var(--bg-secondary);
    color: var(--text-primary);
    &:hover {
      background-color: var(--border-color);
    }
  `}
`;

const InterestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InterestCard = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const InterestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const InterestName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
`;

const InterestActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  font-size: 1.25rem;
  
  &:hover {
    background-color: var(--bg-secondary);
    color: var(--primary-color);
  }
  
  &.delete:hover {
    color: #ef4444;
  }
`;

const InterestMeta = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const InterestDescription = styled.p`
  color: var(--text-primary);
  margin: 0;
  line-height: 1.6;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
`;

const EmptyText = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const PhotoUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PhotoUploadHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const PhotoUploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px dashed var(--border-color);
  border-radius: 0.5rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const PhotoPreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 0.75rem;
`;

const PhotoPreviewItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid var(--border-color);
`;

const PhotoPreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemovePhotoButton = styled.button`
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.65);
  color: white;
  cursor: pointer;
  font-size: 0.85rem;
  line-height: 1;
`;

const InterestPhotos = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
`;

const InterestPhoto = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
`;

const SectionHeading = styled.h2`
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.35rem;
`;

const SectionSubheading = styled.p`
  margin: 0 0 1rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
`;

const UploadedPhotosSection = styled.section`
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const UploadedPhotosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
`;

const UploadedPhotoCard = styled.button`
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--bg-primary);
  padding: 0;
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const UploadedPhotoImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
`;

const UploadedPhotoCaption = styled.span`
  display: block;
  padding: 0.5rem 0.65rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreferencesSection = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: var(--shadow-sm);
`;

const PreferenceField = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PreferenceLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const PreferenceTextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.6;
  }
`;

const MyInterestsPage = () => {
  const MAX_INTEREST_PHOTOS = 5;
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInterest, setEditingInterest] = useState(null);
  const [formData, setFormData] = useState({
    item_name: '',
    category: '',
    description: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [savingInterest, setSavingInterest] = useState(false);

  const [preferences, setPreferences] = useState({
    shopping_for: '',
    style_description: '',
    want_more_of: ''
  });

  const categories = PRE_OWNED_ITEM_CATEGORIES;

  useEffect(() => {
    fetchInterests();
    loadPreferences();
  }, []);

  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem('user_preferences');
      if (saved) {
        const parsed = JSON.parse(saved);
        setPreferences({
          shopping_for: parsed.shopping_for || '',
          style_description: parsed.style_description || '',
          want_more_of: parsed.want_more_of || ''
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = (newPreferences) => {
    try {
      localStorage.setItem('user_preferences', JSON.stringify(newPreferences));
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const handlePreferenceChange = (field, value) => {
    const sanitized = sanitizeUserText(value, MAX_PREFERENCE_TEXT);
    const updated = { ...preferences, [field]: sanitized };
    setPreferences(updated);
    savePreferences(updated);
  };

  const allUploadedPhotos = useMemo(
    () =>
      interests
        .flatMap((interest) =>
          (interest.images || []).map((image) => ({
            ...image,
            interestId: interest.id,
            interestName: interest.item_name,
          }))
        )
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [interests],
  );

  const fetchInterests = async () => {
    try {
      setLoading(true);
      const data = await authAPI.getInterests();
      setInterests(data);
    } catch (error) {
      console.error('Error fetching interests:', error);
      toast.error('Failed to load your interests');
    } finally {
      setLoading(false);
    }
  };

  const resetImageState = () => {
    selectedImages.forEach((image) => URL.revokeObjectURL(image.preview));
    setSelectedImages([]);
    setExistingImages([]);
    setRemovedImageIds([]);
  };

  const handleOpenModal = (interest = null) => {
    resetImageState();
    if (interest) {
      setEditingInterest(interest);
      setFormData({
        item_name: interest.item_name,
        category: interest.category || '',
        description: interest.description || ''
      });
      setExistingImages(interest.images || []);
    } else {
      setEditingInterest(null);
      setFormData({
        item_name: '',
        category: '',
        description: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingInterest(null);
    setFormData({
      item_name: '',
      category: '',
      description: ''
    });
    resetImageState();
  };

  const handleImageChange = (e) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      toast.error('Please select image files only');
      return;
    }

    const remainingSlots = MAX_INTEREST_PHOTOS - existingImages.length + removedImageIds.length - selectedImages.length;
    if (remainingSlots <= 0) {
      toast.error(`You can upload up to ${MAX_INTEREST_PHOTOS} photos per interest.`);
      return;
    }

    const validFiles = [];
    for (const file of imageFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" has an unsupported format. Use JPEG, PNG, GIF, or WebP.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" is too large (max 10MB).`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const acceptedFiles = validFiles.slice(0, remainingSlots);
    if (acceptedFiles.length < validFiles.length) {
      toast.info(`Only ${acceptedFiles.length} photo(s) were added due to the ${MAX_INTEREST_PHOTOS}-photo limit.`);
    }

    const newImages = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setSelectedImages((prev) => [...prev, ...newImages]);
    e.target.value = '';
  };

  const removeSelectedImage = (index) => {
    setSelectedImages((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].preview);
      next.splice(index, 1);
      return next;
    });
  };

  const removeExistingImage = (imageId) => {
    setExistingImages((prev) => prev.filter((image) => image.id !== imageId));
    setRemovedImageIds((prev) => [...prev, imageId]);
  };

  const totalPhotoCount = existingImages.length + selectedImages.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      item_name: sanitizeUserText(formData.item_name, MAX_INTEREST_ITEM_NAME),
      category: formData.category,
      description: sanitizeUserText(formData.description, MAX_INTEREST_DESCRIPTION),
    };
    if (!payload.item_name.trim()) {
      toast.error('Item name is required');
      return;
    }
    try {
      setSavingInterest(true);
      const imageFiles = selectedImages.map((image) => image.file);
      if (editingInterest) {
        await authAPI.updateInterest(editingInterest.id, payload, {
          imageFiles,
          removeImageIds: removedImageIds,
        });
        toast.success('Interest updated successfully');
      } else {
        await authAPI.createInterest(payload, imageFiles);
        toast.success('Interest added successfully');
      }
      handleCloseModal();
      fetchInterests();
    } catch (error) {
      console.error('Error saving interest:', error);
      toast.error(getInterestErrorMessage(error, 'Failed to save interest'));
    } finally {
      setSavingInterest(false);
    }
  };

  const handleDelete = async (interestId) => {
    if (!window.confirm('Are you sure you want to delete this interest?')) {
      return;
    }
    try {
      await authAPI.deleteInterest(interestId);
      toast.success('Interest deleted successfully');
      fetchInterests();
    } catch (error) {
      console.error('Error deleting interest:', error);
      toast.error('Failed to delete interest');
    }
  };

  if (loading) {
    return (
      <InterestsContainer>
        <LoadingSpinner />
      </InterestsContainer>
    );
  }

  return (
    <>
      <SEO title="Interests - FLEA" description="Manage items you're looking for" />
      <InterestsContainer>
        <PageHeader>
          <div>
            <PageTitle>Interests</PageTitle>
            <SubtitleText>Anything specific you're into?</SubtitleText>
          </div>
          <AddButton onClick={() => handleOpenModal()}>+ Add Item I'm Looking For</AddButton>
        </PageHeader>

        <ContextualCoachBanner coachId="interests_want_list" />

        <PreferencesSection>
          <PreferenceField>
            <PreferenceLabel>What are you shopping for lately? (Optional)</PreferenceLabel>
            <PreferenceTextArea
              value={preferences.shopping_for}
              onChange={(e) => handlePreferenceChange('shopping_for', e.target.value)}
              placeholder="Tell us what you've been looking for..."
              maxLength={MAX_PREFERENCE_TEXT}
            />
          </PreferenceField>
          <PreferenceField>
            <PreferenceLabel>Describe your style (Optional)</PreferenceLabel>
            <PreferenceTextArea
              value={preferences.style_description}
              onChange={(e) => handlePreferenceChange('style_description', e.target.value)}
              placeholder="Share your style preferences..."
              maxLength={MAX_PREFERENCE_TEXT}
            />
          </PreferenceField>
          <PreferenceField>
            <PreferenceLabel>What do you want more of? (Optional)</PreferenceLabel>
            <PreferenceTextArea
              value={preferences.want_more_of}
              onChange={(e) => handlePreferenceChange('want_more_of', e.target.value)}
              placeholder="What items or categories are you interested in exploring more?"
              maxLength={MAX_PREFERENCE_TEXT}
            />
          </PreferenceField>
        </PreferencesSection>

        {interests.length === 0 ? (
          <EmptyState>
            <EmptyIcon>🔍</EmptyIcon>
            <EmptyTitle>No Interests Yet</EmptyTitle>
            <EmptyText>
              Add items you're searching for or interested in. This helps us show you relevant items!
            </EmptyText>
            <AddButton onClick={() => handleOpenModal()}>+ Add Your First Interest</AddButton>
          </EmptyState>
        ) : (
          <>
            <SectionHeading>Items you&apos;re looking for</SectionHeading>
            <SectionSubheading>
              Reference photos you add here help sellers understand what you want.
            </SectionSubheading>
            <InterestsList>
            {interests.map((interest) => (
              <InterestCard key={interest.id}>
                <InterestHeader>
                  <InterestName>{interest.item_name}</InterestName>
                  <InterestActions>
                    <ActionButton onClick={() => handleOpenModal(interest)} title="Edit">
                      ✏️
                    </ActionButton>
                    <ActionButton 
                      className="delete" 
                      onClick={() => handleDelete(interest.id)}
                      title="Delete"
                    >
                      🗑️
                    </ActionButton>
                  </InterestActions>
                </InterestHeader>
                <InterestMeta>
                  {interest.category && <span>📁 {interest.category}</span>}
                  <span>📅 {new Date(interest.created_at).toLocaleDateString()}</span>
                </InterestMeta>
                {interest.images?.length > 0 && (
                  <InterestPhotos>
                    {interest.images.map((image) => (
                      <InterestPhoto key={image.id} src={image.url} alt={interest.item_name} />
                    ))}
                  </InterestPhotos>
                )}
                {interest.description && (
                  <InterestDescription>{interest.description}</InterestDescription>
                )}
              </InterestCard>
            ))}
            </InterestsList>
          </>
        )}

        {allUploadedPhotos.length > 0 && (
          <UploadedPhotosSection>
            <SectionHeading>Your uploaded reference photos</SectionHeading>
            <SectionSubheading>
              Everything you&apos;ve uploaded across your interests ({allUploadedPhotos.length} photo
              {allUploadedPhotos.length === 1 ? '' : 's'}).
            </SectionSubheading>
            <UploadedPhotosGrid>
              {allUploadedPhotos.map((photo) => (
                <UploadedPhotoCard
                  key={`${photo.interestId}-${photo.id}`}
                  type="button"
                  onClick={() => {
                    const interest = interests.find((item) => item.id === photo.interestId);
                    if (interest) handleOpenModal(interest);
                  }}
                >
                  <UploadedPhotoImage src={photo.url} alt={photo.interestName} />
                  <UploadedPhotoCaption>{photo.interestName}</UploadedPhotoCaption>
                </UploadedPhotoCard>
              ))}
            </UploadedPhotosGrid>
          </UploadedPhotosSection>
        )}

        <ModalOverlay isOpen={isModalOpen} onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingInterest ? 'Edit Interest' : 'Add New Interest'}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Item Name *</Label>
                <Input
                  type="text"
                  value={formData.item_name}
                  onChange={(e) => setFormData({
                    ...formData,
                    item_name: sanitizeUserText(e.target.value, MAX_INTEREST_ITEM_NAME),
                  })}
                  placeholder="e.g., Nike Air Max 42, Harry Potter book set"
                  maxLength={MAX_INTEREST_ITEM_NAME}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Description (Optional)</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({
                    ...formData,
                    description: sanitizeUserText(e.target.value, MAX_INTEREST_DESCRIPTION),
                  })}
                  placeholder="Add any additional details about what you're looking for..."
                  maxLength={MAX_INTEREST_DESCRIPTION}
                />
              </FormGroup>
              <FormGroup>
                <Label>Photos (Optional)</Label>
                <PhotoUploadSection>
                  <PhotoUploadHint>
                    Upload up to {MAX_INTEREST_PHOTOS} reference photos so sellers know what you want.
                    {totalPhotoCount > 0 ? ` (${totalPhotoCount}/${MAX_INTEREST_PHOTOS} added)` : ''}
                  </PhotoUploadHint>
                  {totalPhotoCount < MAX_INTEREST_PHOTOS && (
                    <PhotoUploadButton htmlFor="interest-photos">
                      📷 Add photos
                      <HiddenFileInput
                        id="interest-photos"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                      />
                    </PhotoUploadButton>
                  )}
                  {(existingImages.length > 0 || selectedImages.length > 0) && (
                    <PhotoPreviewGrid>
                      {existingImages.map((image) => (
                        <PhotoPreviewItem key={`existing-${image.id}`}>
                          <PhotoPreviewImg src={image.url} alt="Interest reference" />
                          <RemovePhotoButton
                            type="button"
                            onClick={() => removeExistingImage(image.id)}
                            aria-label="Remove photo"
                          >
                            ×
                          </RemovePhotoButton>
                        </PhotoPreviewItem>
                      ))}
                      {selectedImages.map((image, index) => (
                        <PhotoPreviewItem key={`new-${image.preview}`}>
                          <PhotoPreviewImg src={image.preview} alt={`New interest photo ${index + 1}`} />
                          <RemovePhotoButton
                            type="button"
                            onClick={() => removeSelectedImage(index)}
                            aria-label="Remove photo"
                          >
                            ×
                          </RemovePhotoButton>
                        </PhotoPreviewItem>
                      ))}
                    </PhotoPreviewGrid>
                  )}
                </PhotoUploadSection>
              </FormGroup>
              <ButtonGroup>
                <Button type="button" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" primary disabled={savingInterest}>
                  {savingInterest ? 'Saving...' : editingInterest ? 'Update' : 'Add'} Interest
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      </InterestsContainer>
    </>
  );
};

export default MyInterestsPage;
